import { type Lap } from '../../domain/lap/Lap'
import { type SpatialBucket } from '../../domain/telemetry/spatial/SpatialBucket'
import { type GpsPoint } from '../../domain/telemetry/processed/GpsPoint'
import { formatTimeSpan } from '../../utils/timeUtils'
import { createLapPoint, type LapPoint } from '../../domain/lap/LapPoint'
import { type CoastingPeriod, type CoastingConfig } from './types'
import { calculateCoastingIntensity } from './physics'
import type { VehicleStrategy } from './vehicles/VehicleStrategy'

/**
 * Thresholds for acceleration signal selection
 */
const NEAR_ZERO_ACCELERATION_THRESHOLD_MS2 = 0.5 // m/s²
const MIN_RAW_ACCELERATION_SIGNAL_MS2 = 0.8 // m/s² - lowered to catch gentle braking

/**
 * Select best acceleration for classification.
 *
 * Why both raw and smoothed? Smoothing reduces GPS noise but can crush
 * weak signals (-1.5 → -0.3 m/s²) OR reverse the sign entirely.
 *
 * Use raw acceleration when:
 * 1. Smoothed is near-zero but raw shows significant movement, OR
 * 2. Smoothed has opposite sign from raw (smoothing crossed zero)
 *
 * This prevents:
 * - Weak braking signals being lost to smoothing
 * - Sign reversals from phase-shifted Gaussian smoothing
 */
function selectAccelerationForClassification(
  rawAcceleration: number | undefined,
  smoothedAcceleration: number
): number {
  if (rawAcceleration === undefined) {
    return smoothedAcceleration
  }

  const smoothedIsNearZero =
    Math.abs(smoothedAcceleration) < NEAR_ZERO_ACCELERATION_THRESHOLD_MS2
  const rawIsSignificant =
    Math.abs(rawAcceleration) >= MIN_RAW_ACCELERATION_SIGNAL_MS2

  // Use raw if smoothed crushed a significant signal to near-zero
  if (smoothedIsNearZero && rawIsSignificant) {
    return rawAcceleration
  }

  // Use raw if smoothing reversed the sign (e.g., braking → acceleration)
  const signsAreDifferent =
    Math.sign(rawAcceleration) !== Math.sign(smoothedAcceleration) &&
    rawAcceleration !== 0 &&
    smoothedAcceleration !== 0

  if (signsAreDifferent && rawIsSignificant) {
    return rawAcceleration
  }

  return smoothedAcceleration
}

/**
 * Create a LapPoint from a lap and GPS point
 */
function createLapPointFromGpsPoint(lap: Lap, gpsPoint: GpsPoint): LapPoint {
  return createLapPoint(lap.id, lap.lapNumber, gpsPoint)
}

/**
 * Classify coasting intensity from spatial buckets
 *
 * @param buckets - Pre-calculated spatial buckets from Lap
 * @param lap - Lap metadata and telemetry
 * @param config - Coasting detection configuration
 * @param vehicleStrategy - Vehicle-specific coasting rules
 * @returns Array of coasting periods with intensity scores
 */
function classifyCoastingFromBuckets(
  buckets: SpatialBucket[],
  lap: Lap,
  config: CoastingConfig,
  vehicleStrategy: VehicleStrategy
): CoastingPeriod[] {
  const coastingPeriods: CoastingPeriod[] = []

  buckets.forEach((bucket, index) => {
    // Skip first bucket - no acceleration data
    if (index === 0) return

    // Skip if below minimum speed
    if (bucket.speedKmh < config.minimumSpeedForAnalysis) return

    // Get metrics from pre-calculated bucket
    const rawAcceleration: number | undefined = bucket.rawAcceleration
    const smoothedAcceleration: number = bucket.smoothedAcceleration
    const smoothedLeanAngle: number = bucket.smoothedLeanAngle
    const speedKmh: number = bucket.speedKmh
    const gpsIndex: number = bucket.gpsIndex

    // Select best acceleration for classification (raw vs smoothed)
    // This preserves weak signals that heavy smoothing might eliminate
    const accelerationForClassification = selectAccelerationForClassification(
      rawAcceleration,
      smoothedAcceleration
    )

    // Calculate coasting intensity
    const intensity = calculateCoastingIntensity(
      accelerationForClassification,
      smoothedLeanAngle,
      config,
      speedKmh,
      vehicleStrategy
    )

    // Get GPS point for location data
    const gpsPoint = lap.processedTelemetryData.gpsPoints[gpsIndex]
    const lapPoint = createLapPointFromGpsPoint(lap, gpsPoint)

    // Calculate lap-relative elapsed time (not session-relative)
    // This is critical for comparing laps on the same time axis
    const lapRelativeElapsedMs =
      gpsPoint.timestamp.getTime() - lap.startTime.getTime()
    const lapRelativeTimeString =
      formatTimeSpan(lapRelativeElapsedMs, {
        granularity: 'tenths',
        format: 'mm:ss.S',
      }) ?? ''

    const coastingPeriod: CoastingPeriod = {
      lapId: lap.id,
      timeBasedLocation: lapPoint,
      distanceBasedLocation: lapPoint,
      coastingIntensity: intensity,
      acceleration: rawAcceleration ?? smoothedAcceleration,
      smoothedAcceleration,
      leanAngle: smoothedLeanAngle,
      speed: bucket.speedKmh,
      time: lapRelativeTimeString,
      distance: bucket.distance,
    }

    coastingPeriods.push(coastingPeriod)
  })

  return coastingPeriods
}

/**
 * Main coasting detection pipeline using centralized spatial buckets
 *
 * @param laps - Laps to analyze (must have spatialBuckets populated)
 * @param config - Coasting detection configuration
 * @param vehicleStrategy - Vehicle-specific coasting rules
 * @returns Array of coasting periods across all laps
 */
export function collectAndClassifyCoastingData(
  laps: readonly Lap[],
  config: CoastingConfig,
  vehicleStrategy: VehicleStrategy
): readonly CoastingPeriod[] {
  const allCoastingPeriods: CoastingPeriod[] = []

  laps.forEach((lap) => {
    // Ensure lap has spatial buckets
    if (!lap.spatialBuckets || lap.spatialBuckets.length === 0) {
      return
    }

    // Classify coasting intensity using pre-calculated buckets
    const spatialBuckets: SpatialBucket[] = lap.spatialBuckets
    const coastingPeriods = classifyCoastingFromBuckets(
      spatialBuckets,
      lap,
      config,
      vehicleStrategy
    )

    allCoastingPeriods.push(...coastingPeriods)
  })

  return allCoastingPeriods
}
